'use client';

import type { FC } from 'react';
import { tmaActions } from '../../bootstrap/tmaActions';
import { ScreenContent, ScreenFixedTop, ScreenTabBar } from '../../screen';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { Credits } from '../../components/Credits';
import translations from './RecruiterGuideScreen.translations';

const stepNumberColors: Record<string, string> = {
  purple: 'bg-[linear-gradient(135deg,#a855f7_0%,#ec4899_100%)]',
  blue: 'bg-[linear-gradient(135deg,#3b82f6_0%,#06b6d4_100%)]',
  green: 'bg-[linear-gradient(135deg,#22c55e_0%,#10b981_100%)]',
  yellow: 'bg-[linear-gradient(135deg,#eab308_0%,#f97316_100%)]',
};

export const RecruiterGuideScreen: FC = () => {
  const { t } = useLocalTranslation(translations);

  const handleContactClick = () => {
    void tmaActions.openTelegramLink('https://t.me/+vd6jfp1BEhQ1ODEy');
  };

  return (
    <>
      <ScreenFixedTop title={t('title')} />
      <ScreenTabBar />
      <ScreenContent>
        <div className="flex flex-col gap-6 p-4 pb-8">
          {/* How it works */}
          <div className="bg-slate-800 rounded-xl p-5 border border-slate-600">
            <h2 className="text-xl font-bold text-white mb-6">{t('howItWorks')}</h2>

            <div className="flex flex-col gap-2">
              {/* Step 1 */}
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-[28px] h-[28px] rounded-full flex items-center justify-center shrink-0 ${stepNumberColors.purple}`}>
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <h3 className="text-white font-bold text-base m-0">{t('step1Title')}</h3>
                </div>
                <div className="flex flex-col gap-3">
                  <p className="text-[rgb(209,213,219)] text-sm leading-normal m-0">{t('step1Description')}</p>
                  <ul className="flex flex-col gap-1 pl-0 m-0 list-none">
                    <li className="flex items-start gap-1.5 text-[rgb(209,213,219)] text-sm leading-snug [&>i]:text-purple-500 [&>i]:text-sm [&>i]:mt-0.5 [&>i]:shrink-0">
                      <i className="ri-checkbox-circle-fill" />
                      <span>{t('step1Item1')}</span>
                    </li>
                    <li className="flex items-start gap-1.5 text-[rgb(209,213,219)] text-sm leading-snug [&>i]:text-purple-500 [&>i]:text-sm [&>i]:mt-0.5 [&>i]:shrink-0">
                      <i className="ri-checkbox-circle-fill" />
                      <span>{t('step1Item2')}</span>
                    </li>
                    <li className="flex items-start gap-1.5 text-[rgb(209,213,219)] text-sm leading-snug [&>i]:text-purple-500 [&>i]:text-sm [&>i]:mt-0.5 [&>i]:shrink-0">
                      <i className="ri-checkbox-circle-fill" />
                      <span>{t('step1Item3')}</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 2 */}
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-[28px] h-[28px] rounded-full flex items-center justify-center shrink-0 ${stepNumberColors.blue}`}>
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <h3 className="text-white font-bold text-base m-0">{t('step2Title')}</h3>
                </div>
                <div className="flex flex-col gap-3">
                  <p className="text-[rgb(209,213,219)] text-sm leading-normal m-0">{t('step2Description')}</p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-[28px] h-[28px] rounded-full flex items-center justify-center shrink-0 ${stepNumberColors.green}`}>
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <h3 className="text-white font-bold text-base m-0">{t('step3Title')}</h3>
                </div>
                <div className="flex flex-col gap-3">
                  <p className="text-[rgb(209,213,219)] text-sm leading-normal m-0">
                    {t('step3Description')}{' '}
                    <span className="text-purple-500 font-semibold underline cursor-pointer" onClick={handleContactClick}>
                      {t('step3Channel')}
                    </span>
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-[28px] h-[28px] rounded-full flex items-center justify-center shrink-0 ${stepNumberColors.yellow}`}>
                    <span className="text-white font-bold text-sm">4</span>
                  </div>
                  <h3 className="text-white font-bold text-base m-0">{t('step4Title')}</h3>
                </div>
                <div className="flex flex-col gap-3">
                  <p className="text-[rgb(209,213,219)] text-sm leading-normal m-0">
                    {t('step4DescriptionPrefix')} <Credits amount={250000} /> {t('step4DescriptionMiddle')}{' '}
                    {t('step4DescriptionViews')}! {t('step4DescriptionSuffix')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Warning section */}
          <div className="bg-[linear-gradient(135deg,rgba(127,29,29,0.4)_0%,rgba(154,52,18,0.4)_100%)] rounded-xl p-5 border border-red-500/30">
            <h2 className="text-white font-bold text-lg mb-3 flex items-center gap-2 [&>i]:text-red-400 [&>i]:text-xl">
              <i className="ri-alert-line" />
              <span>{t('warningTitle')}</span>
            </h2>
            <div className="flex flex-col gap-3">
              <div className="bg-black/30 rounded-lg p-3">
                <p className="text-white/90 text-sm leading-normal">
                  <span className="font-bold text-red-400">{t('warningCheck')}</span>{' '}
                  {t('warningCheckText')}
                </p>
              </div>
              <div className="bg-black/30 rounded-lg p-3">
                <p className="text-white/90 text-sm leading-normal">
                  <span className="font-bold text-red-400">{t('warningBan')}</span> {t('warningBanText')}
                </p>
              </div>
              <div className="bg-black/30 rounded-lg p-3">
                <p className="text-white/90 text-sm leading-normal">
                  <span className="font-bold text-green-400">{t('warningHonest')}</span>{' '}
                  {t('warningHonestText')}
                </p>
              </div>
            </div>
          </div>

          {/* Contact section */}
          <div className="bg-[linear-gradient(135deg,rgba(30,58,138,0.4)_0%,rgba(22,78,99,0.4)_100%)] rounded-xl p-5 border border-blue-500/30">
            <h2 className="text-white font-bold text-lg mb-3 flex items-center gap-2 [&>i]:text-blue-400 [&>i]:text-xl">
              <i className="ri-customer-service-2-line" />
              <span>{t('contactTitle')}</span>
            </h2>
            <p className="text-white/80 text-sm leading-normal mb-4">{t('contactDescription')}</p>
            <button className="w-full bg-[linear-gradient(135deg,#3b82f6_0%,#06b6d4_100%)] text-white font-bold p-3 rounded-xl border-none cursor-pointer flex items-center justify-center gap-2 text-base transition-all duration-200 ease-smooth shadow-[0_4px_12px_rgba(59,130,246,0.3)] hover:-translate-y-px hover:shadow-[0_6px_16px_rgba(59,130,246,0.4)] active:translate-y-0 [&>i]:text-xl" onClick={handleContactClick}>
              <i className="ri-telegram-line" />
              <span>{t('contactButton')}</span>
            </button>
          </div>
        </div>
      </ScreenContent>
    </>
  );
};
